import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Receipt, CheckCircle2, Clock, XCircle, Download, ChevronRight } from "lucide-react";

export const fmtBillingDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export const BILLING_STATUS = {
  paid: { label: "Paid", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]", Icon: CheckCircle2 },
  initiated: { label: "Pending", cls: "bg-[#E9C46A]/30 text-[#B45309]", Icon: Clock },
  open: { label: "Pending", cls: "bg-[#E9C46A]/30 text-[#B45309]", Icon: Clock },
  expired: { label: "Expired", cls: "bg-[#1E3F33]/10 text-[#5C6B64]", Icon: XCircle },
};

export const downloadReceipt = async (id) => {
  try {
    const res = await api.get(`/payments/history/${id}/receipt`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipt-${id.slice(-8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not download receipt");
  }
};

export const BillingRow = ({ t }) => {
  const s = BILLING_STATUS[t.payment_status] || BILLING_STATUS.initiated;
  return (
    <li className="py-3 flex items-center justify-between gap-3" data-testid={`billing-row-${t.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#1E3F33] truncate">{t.description}</div>
        <div className="text-xs text-[#8A9A86]">{fmtBillingDate(t.created_at)}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-sm font-medium text-[#1E3F33]">${Number(t.amount).toFixed(2)}</span>
        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${s.cls}`} data-testid={`billing-status-${t.id}`}>
          <s.Icon className="w-3 h-3" /> {s.label}
        </span>
        {t.payment_status === "paid" && (
          <button
            type="button"
            data-testid={`billing-receipt-${t.id}`}
            onClick={() => downloadReceipt(t.id)}
            title="Download PDF receipt"
            className="text-[#5C6B64] hover:text-[#CC5A3A] transition-colors p-1"
          >
            <Download className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
};

export const BillingHistory = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/payments/history", { params: { days: 90 } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  if (items === null) return null;

  return (
    <div className="card-soft" data-testid="billing-history-card">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-1 flex items-center gap-2">
        <Receipt className="w-4 h-4 text-[#2D6A4F]" /> Billing history
      </h2>
      <p className="text-xs text-[#8A9A86] mb-2">Last 90 days</p>
      {items.length === 0 ? (
        <p className="text-sm text-[#8A9A86]" data-testid="billing-empty">No subscription payments in the last 90 days.</p>
      ) : (
        <ul className="divide-y divide-[#1E3F33]/8" data-testid="billing-list">
          {items.map((t) => <BillingRow key={t.id} t={t} />)}
        </ul>
      )}
      <Link
        to="/dashboard/billing"
        data-testid="billing-view-all-link"
        className="mt-4 inline-flex items-center gap-1 text-sm text-[#CC5A3A] hover:underline underline-offset-2"
      >
        View all receipts <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
};
